// The ending (critique decisions 4 and 8): leaving is a short farewell with a way back in, not a drop onto the form. A
// room that ends for another reason (closed, replaced, server error) says why.
// MAJLIS-135 (T-161, UI-C10, consultant ruling 3) kept Rejoin until it was used. MAJLIS-169 (component-rules.md section 1,
// rules 1 and 2; the owner-approved screen-rules mock-up of 2026-10-05, frames (a) and (b)): the floating "Rejoin ×" toast
// is gone - a style nothing else in the app had. Rejoin is now the screen's ONE filled button, under the words, and
// "Back to the start" the text link under it; it stays until it is used (there is nothing to dismiss). A screen whose only
// action is "Back to the start" (a guest's "This majlis has ended", a removed or replaced seat, a denied knock) shows it
// as the filled button: one action is the primary, never a bare text link. The host's ending (Open the record filled,
// Back to the start the link) is unchanged.
import { useI18n } from "../i18n";
import type { Failure } from "../protocol/client";
import { FAILURE_TEXT } from "./EntryScreen";

export function Ending({
  failure,
  onRejoin,
  onStartAgain,
  host = false,
  onOpenRecord,
}: {
  /** MAJLIS-67 (mock-up 1a, ruling 9): this device hosted the room that has just ENDED - the record is one tap away.
   * MAJLIS-66 (mock-up 5c, UI-C15, T-109): everyone else is told to ask the host for a link to the record. */
  host?: boolean;
  onOpenRecord?: () => void;
  failure: Failure | null; // null: the person left
  onRejoin: () => void;
  onStartAgain: () => void;
}) {
  const { t } = useI18n();

  // MAJLIS-37 (UI-C32, the mock-up's 2b and 4d): a knock that ended says so once (role=alert) with one line of what
  // to do. A DENIED knock offers no Try again - this identity may not knock here again - only Back to the start.
  const knockEnd = failure === "denied" || failure === "knock_timeout";
  // the failures a fresh try can mend (Try again is their primary); the rest offer only the way back to the start
  const retry = failure !== null && failure !== "room_closed" && failure !== "replaced" && failure !== "removed" && failure !== "denied";
  // MAJLIS-169: "Back to the start" is the screen's primary when nothing else is (rule 2); otherwise the link under it
  const onlyAction = failure !== null && !retry && !(failure === "room_closed" && host && onOpenRecord);
  return (
    <main className="ending" data-spec={knockEnd ? "UI-S2 UI-C32 UI-C14 D-30" : "UI-S2 UI-C10 P-3"}>
      <div className="ending-words" role={knockEnd ? "alert" : undefined}>
        <h1 className="ending-title">{failure ? t(FAILURE_TEXT[failure]) : t("farewell")}</h1>
        {!failure && <p className="ending-body">{t("farewellBody")}</p>}
        {failure === "denied" && <p className="ending-body">{t("deniedBody")}</p>}
        {failure === "knock_timeout" && <p className="ending-body">{t("knockTimeoutBody")}</p>}
      </div>
      {failure === null && (
        <button type="button" className="primary ending-rejoin" onClick={onRejoin} data-spec="UI-C10 T-161">
          {t("rejoin")}
        </button>
      )}
      {failure === "room_closed" && host && onOpenRecord && (
        <>
          <p className="ending-body" data-spec="UI-C15 D-15 D-16">
            {t("endedHostBody")}
          </p>
          <button type="button" className="primary" onClick={onOpenRecord} data-spec="UI-S3 D-16">
            {t("openRecord")}
          </button>
        </>
      )}
      {failure === "room_closed" && !(host && onOpenRecord) && (
        <p className="ending-body" data-spec="UI-C15 D-16 T-109">
          {t("askHostBody")}
        </p>
      )}
      {retry && (
        <button type="button" className="primary" onClick={onRejoin}>
          {t("tryAgain")}
        </button>
      )}
      <button type="button" className={onlyAction ? "primary" : "text-link"} onClick={onStartAgain}>
        {t("startAgain")}
      </button>
    </main>
  );
}

/** MAJLIS-66 (mock-up 5d, UI-C15, T-109): a read link that no longer opens - turned off, a deleted record, or a link that
 * never was. One screen for all three, on purpose: telling them apart would tell a stranger which links were once real. */
export function LinkDead({ onStartAgain }: { onStartAgain: () => void }) {
  const { t } = useI18n();
  return (
    <main className="ending" role="status" data-spec="UI-C15 D-16 P-33 T-109">
      <h1 className="ending-title">{t("linkDeadTitle")}</h1>
      <p className="ending-body">{t("linkDeadBody")}</p>
      {/* MAJLIS-170 (rule 2; mock-up frame (d)): the one action is the filled button */}
      <button type="button" className="primary" onClick={onStartAgain}>
        {t("startAgain")}
      </button>
    </main>
  );
}
